import { useMemo, useState } from "react";

import { Button, CurrencyWithDiscount } from "@chef/components";
import {
  openModal,
  useProductCategoriesQuery,
  useProductsByCategoriesQuery,
} from "@chef/state-management";
import {
  getBasketDataFromCalendar,
  getDiscountedAmount,
  getImageFromVariation,
  getPriceOfProductByVariation,
  getStandaloneProductCategoryIdsFromProductCategories,
} from "@chef/state-management/helpers";
import { useAppDispatch, useCalendarData } from "@chef/state-management/hooks";
import { removeDuplicatesByKey } from "@chef/utils/removeDuplicates";
import { Controls } from "@chef/smart-components";
import { language } from "@chef/constants";

import type { WeekYear } from "@chef/types/common";

import { ProductCard } from "./tiles/ProductCard";

import { intl } from "../pages/WeekEditorConfirmOrderExperimental.Intl";

const UPSELL_ITEM_STEP = 10;

export const CheckoutExtrasGrid = ({ week, year }: WeekYear) => {
  const dispatch = useAppDispatch();
  const [numUpsellItemsShown, setNumUpsellItemsShown] =
    useState(UPSELL_ITEM_STEP);
  const { calendar } = useCalendarData({
    week,
    year,
  });

  const calendarData = calendar?.filter(
    (c) => c.week === week && c.year === year,
  );

  const basketData = getBasketDataFromCalendar({
    calendar: calendarData,
    week,
    year,
  });
  const { data: productCategoriesQuery } = useProductCategoriesQuery();

  const { data: productsByCategoriesQuery } = useProductsByCategoriesQuery(
    {
      categoryIds: getStandaloneProductCategoryIdsFromProductCategories(
        productCategoriesQuery?.productCategories || [],
      ),
      week,
      year,
    },
    { skip: !productCategoriesQuery },
  );

  const allProducts = useMemo(() => {
    return (
      productsByCategoriesQuery?.productsByCategories.flatMap(
        (category) => category.products,
      ) || []
    );
  }, [productsByCategoriesQuery]);

  const variationsInBasket =
    basketData?.mealboxBasket?.basketDetails.products.map((p) => p.variationId);

  const variationsNotInBasket = useMemo(() => {
    return removeDuplicatesByKey(
      allProducts
        .flatMap((p) => p.variations)
        .filter(
          ({ variationId }) =>
            variationsInBasket?.includes(variationId) === false,
        )
        .sort(
          (a, b) =>
            b.flexiblePriceV2.campaignDiscount -
            a.flexiblePriceV2.campaignDiscount,
        ),
      "variationId",
    );
  }, [allProducts, variationsInBasket]);

  const handleShowMore = () => {
    setNumUpsellItemsShown((prev) =>
      Math.min(prev + UPSELL_ITEM_STEP, variationsNotInBasket.length),
    );
  };

  const openStandaloneProductModal = (
    productId: string,
    variationId: string,
  ) => {
    dispatch(
      openModal({
        id: "STANDALONE_PRODUCT",
        productId,
        variationId,
        week,
        year,
        view: "deviation",
        basketType: "deviation",
      }),
    );
  };

  return (
    <section className="flex flex-col items-center w-full gap-8">
      <div className="grid w-full grid-cols-1 gap-4 lg:grid-cols-2">
        {variationsNotInBasket
          .slice(0, numUpsellItemsShown)
          .map((variation) => {
            const imageUrl = getImageFromVariation({ variation });
            const discount = getDiscountedAmount(variation);
            const totalPrice = getPriceOfProductByVariation(variation);

            return (
              <ProductCard
                key={variation.variationId}
                imageUrl={imageUrl}
                title={variation.name}
                onClick={() => {
                  openStandaloneProductModal(
                    variation.product.productId,
                    variation.variationId,
                  );
                }}
              >
                <CurrencyWithDiscount
                  language={language}
                  discount={discount}
                  total={totalPrice}
                  emphasized
                />
                <Controls
                  basketType="deviation"
                  week={week}
                  year={year}
                  variation={variation}
                />
              </ProductCard>
            );
          })}
      </div>
      {numUpsellItemsShown < variationsNotInBasket.length && (
        <Button
          outlined
          onClick={handleShowMore}
          full
          className="mb-8 col-span-full md:w-auto md:px-32"
          small
        >
          {intl.SHOW_MORE}
        </Button>
      )}
    </section>
  );
};
